/**
 * Photos and Documents page: the parts that are plain functions.
 *
 * Kept out of StoreFiles.tsx for two reasons. react-refresh's
 * only-export-components rule (an error here, lint runs with
 * --max-warnings 0) flags a .tsx file that exports non-components, and
 * Cockpit's vitest projects run in the Node environment, so pure modules are
 * the only part of a page that can be unit-tested. Same split as
 * storeInfoParam.ts next door.
 */

/** Tile or list. Remembered per browser, not per store. */
export type StoreFilesView = 'tile' | 'list';

export const STORE_FILES_VIEW_KEY = 'cockpit.storeFiles.view';

export function readStoreFilesView(): StoreFilesView {
  try {
    return window.localStorage.getItem(STORE_FILES_VIEW_KEY) === 'list' ? 'list' : 'tile';
  } catch {
    // Private mode / disabled storage: fall back to the default view.
    return 'tile';
  }
}

export function writeStoreFilesView(view: StoreFilesView): void {
  try {
    window.localStorage.setItem(STORE_FILES_VIEW_KEY, view);
  } catch {
    // Ignore storage errors (private mode, quota, etc.).
  }
}

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'] as const;

/**
 * Human file size: "812 B", "1.4 MB". Powers of 1024, one decimal above KB,
 * "--" for a size the API did not record.
 */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return '--';
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < BYTE_UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const label = BYTE_UNITS[unit] ?? 'B';
  return `${value >= 10 ? Math.round(value) : Math.round(value * 10) / 10} ${label}`;
}

/** Splits a list into runs of at most `size`. Used for the 50-per-POST presign batches. */
export function chunk<T>(items: T[], size: number): T[][] {
  if (size < 1) return items.length ? [items] : [];
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/** The directory part of a `webkitRelativePath`, '' for a file at the top. */
export function folderPathOf(relativePath: string): string {
  const parts = relativePath.split('/').filter(Boolean);
  parts.pop();
  return parts.join('/');
}

/**
 * The folders a directory upload has to create, parents before children and
 * each one once, so the caller can POST them top-down and always know the
 * parent id by the time it needs it.
 *
 * Input is every selected file's `webkitRelativePath`
 * ("Store 101/Permits/a.pdf"); output is the distinct directory paths
 * ("Store 101", "Store 101/Permits").
 */
export function planFolderUpload(relativePaths: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const relativePath of relativePaths) {
    const parts = folderPathOf(relativePath).split('/').filter(Boolean);
    let path = '';
    for (const part of parts) {
      path = path ? `${path}/${part}` : part;
      if (!seen.has(path)) {
        seen.add(path);
        out.push(path);
      }
    }
  }
  // Shortest first, so a nested path never lands before the folder it sits in
  // even when the browser hands the files over in an odd order.
  return out.sort((a, b) => a.split('/').length - b.split('/').length);
}

/** Splits "a/b/c" into its parent path ("a/b") and leaf name ("c"). */
export function splitFolderPath(path: string): { parent: string; name: string } {
  const parts = path.split('/').filter(Boolean);
  const name = parts.pop() ?? '';
  return { parent: parts.join('/'), name };
}

export function isImageContentType(contentType: string | null | undefined): boolean {
  return !!contentType && contentType.toLowerCase().startsWith('image/');
}

/** Which glyph a file gets when there is no thumbnail. The page maps it to an icon. */
export type FileKind = 'image' | 'pdf' | 'sheet' | 'doc' | 'archive' | 'file';

const EXTENSION_KINDS: Record<string, FileKind> = {
  pdf: 'pdf',
  csv: 'sheet',
  xls: 'sheet',
  xlsx: 'sheet',
  doc: 'doc',
  docx: 'doc',
  txt: 'doc',
  rtf: 'doc',
  zip: 'archive',
  gz: 'archive',
  rar: 'archive',
  '7z': 'archive',
};

export function fileKind(name: string, contentType: string | null | undefined): FileKind {
  if (isImageContentType(contentType)) return 'image';
  const ct = (contentType ?? '').toLowerCase();
  if (ct === 'application/pdf') return 'pdf';
  const ext = name.includes('.') ? (name.split('.').pop() ?? '').toLowerCase() : '';
  return EXTENSION_KINDS[ext] ?? 'file';
}

/**
 * A folder name the API will accept: trimmed, no slashes, 1 to 255 chars.
 * Returns the reason it is rejected, or null when it is fine, so the New
 * folder and Rename modals can disable their button and say why.
 */
export function folderNameError(name: string): string | null {
  const trimmed = name.trim();
  if (!trimmed) return 'Enter a name.';
  if (trimmed.length > 255) return 'Name is too long (255 characters max).';
  if (trimmed.includes('/') || trimmed.includes('\\')) return 'Name cannot contain / or \\.';
  return null;
}
